/**
 * Error Reporting Service
 * 
 * Reports client-side errors to the backend for tracking and debugging.
 */

import { env } from '../utils/env';
import { loggedFetch } from '../utils/apiLogger';
import { getFirebaseIdToken as getToken } from '../utils/firebaseToken';

/**
 * Get Firebase ID token for API authentication (optional)
 */
async function getFirebaseIdToken(): Promise<string | null> {
  try {
    return await getToken();
  } catch {
    return null;
  }
}

/**
 * Report Error Response
 */
export interface ReportErrorResponse {
  id: string;
}

/**
 * Report an error to the backend
 * 
 * @param code - Error code (e.g., "ai_error", "network_error")
 * @param message - Error message
 * @param requestId - Optional request ID for tracking
 */
export async function reportError(
  code: string,
  message: string,
  requestId?: string,
): Promise<ReportErrorResponse | null> {
  // Don't report errors in offline mode
  if (!navigator.onLine) {
    return null;
  }

  try {
    const token = await getFirebaseIdToken();
    
    const url = `${env.apiBaseUrl}/v1/errors/report`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await loggedFetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        code,
        message,
        requestId,
      }),
    });

    if (!response.ok) {
      await response.json().catch(() => ({ message: 'Unknown error' }));
      return null;
    }

    const result = await response.json() as ReportErrorResponse;
    return result;
  } catch {
    // Silently fail - don't throw errors from error reporting
    return null;
  }
}

/**
 * Helper to report AI errors
 */
export async function reportAIError(
  message: string,
  requestId?: string,
): Promise<void> {
  await reportError('ai_error', message, requestId);
}

/**
 * Helper to report network errors
 */
export async function reportNetworkError(
  message: string,
  requestId?: string,
): Promise<void> {
  await reportError('network_error', message, requestId);
}

/**
 * Helper to report unexpected errors
 */
export async function reportUnexpectedError(
  message: string,
  requestId?: string,
): Promise<void> {
  await reportError('unexpected_error', message, requestId);
}
